import { useEffect } from 'react';
import { API_CONFIG, getApiUrl } from '@/config/apiConfig';
import { kioskHeartbeatIntervalSeconds } from '@/config/surfaceEnv';
import { useSurface } from '@/contexts/SurfaceContext';
import { buildRequestSigningHeaders } from '@/utils/requestSigning';

const HEARTBEAT_INTERVAL_DEFAULT_MS = 60 * 1000; // 60 seconds

/** VITE_KIOSK_HEARTBEAT_INTERVAL_SECONDS (never below 60 s), else 60 s. */
function heartbeatIntervalMs(): number {
  const env = kioskHeartbeatIntervalSeconds();
  if (!env) return HEARTBEAT_INTERVAL_DEFAULT_MS;
  const seconds = Number.parseInt(env, 10);
  return Number.isFinite(seconds) ? Math.max(60, seconds) * 1000 : HEARTBEAT_INTERVAL_DEFAULT_MS;
}

/**
 * Sends a POST /api/kiosk/heartbeat every 60 seconds (VITE_KIOSK_HEARTBEAT_INTERVAL_SECONDS) so
 * the backend can detect when a kiosk goes offline and send a Telegram alert.
 *
 * Runs only where the surface can('heartbeat') (kiosks, src/config/surfacePolicy.ts); an unknown
 * surface waits until it resolves. Fire-and-forget: failures are silently ignored so they never
 * affect UX.
 */
export function useKioskHeartbeat() {
  const enabled = useSurface().can('heartbeat');

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const ping = async () => {
      try {
        const signingHeaders = await buildRequestSigningHeaders('POST', API_CONFIG.ENDPOINTS.KIOSK.HEARTBEAT);
        if (cancelled) return;
        await fetch(getApiUrl(API_CONFIG.ENDPOINTS.KIOSK.HEARTBEAT), {
          method: 'POST',
          credentials: 'include',
          headers: signingHeaders,
        });
      } catch {
        /* ignore */
      }
    };

    void ping();
    const interval = setInterval(() => void ping(), heartbeatIntervalMs());

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled]);
}
